import { Autocomplete } from 'shared-ui';

import { OwnerHistoryActionDescriptionEnum } from 'api/owners/owners.enum';
import { HistoryActionEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';

import * as Styled from './HistoryActionAutocomplete.styles';
import { HistoryActionAutocompleteProps, HistoryActionDescriptionEnum } from './HistoryActionAutocomplete.types';

const BASE_ACTIONS = [
  HistoryActionEnum.ZABLOKOWANO,
  HistoryActionEnum.ODBLOKOWANO,
  HistoryActionEnum.ZWROCONO,
  HistoryActionEnum.WYDANO,
  HistoryActionEnum.ZARCHIWIZOWANO,
  HistoryActionEnum.PRZYWROCONO,
  HistoryActionEnum.DODANO,
  HistoryActionEnum.ZEDYTOWANO,
] as const;

export const HistoryActionAutocomplete = ({
  selectedAction,
  onChangeAction,
  historyType,
}: HistoryActionAutocompleteProps) => {
  const { formatMessage } = useLocale();

  const currentConfig = {
    owners: BASE_ACTIONS.map((value) => ({
      value,
      label: formatMessage({ id: OwnerHistoryActionDescriptionEnum[value] }),
    })),
    cards: BASE_ACTIONS.map((value) => ({
      value,
      label: formatMessage({ id: HistoryActionDescriptionEnum[value] }),
    })),
    groups: (
      [
        HistoryActionEnum.ZARCHIWIZOWANO,
        HistoryActionEnum.PRZYWROCONO,
        HistoryActionEnum.DODANO,
        HistoryActionEnum.ZEDYTOWANO,
      ] as const
    ).map((value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) })),

    groupPermissions: (
      [HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const
    ).map((value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) })),

    ownersEffectivePermissions: (
      [HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const
    ).map((value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) })),

    control: ([HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const).map(
      (value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) }),
    ),

    wanted: ([HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const).map(
      (value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) }),
    ),

    announcements: ([HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const).map(
      (value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) }),
    ),

    schedules: ([HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO, HistoryActionEnum.USUNIETO] as const).map(
      (value) => ({ value, label: formatMessage({ id: HistoryActionDescriptionEnum[value] }) }),
    ),

    sequences: ([HistoryActionEnum.DODANO, HistoryActionEnum.USUNIETO] as const).map((value) => ({
      value,
      label: formatMessage({ id: HistoryActionDescriptionEnum[value] }),
    })),

    usersLogin: ([HistoryActionEnum.ZALOGOWANO, HistoryActionEnum.WYLOGOWANO] as const).map((value) => ({
      value,
      label: formatMessage({ id: HistoryActionDescriptionEnum[value] }),
    })),

    user: ([HistoryActionEnum.DODANO, HistoryActionEnum.ZEDYTOWANO] as const).map((value) => ({
      value,
      label: formatMessage({ id: HistoryActionDescriptionEnum[value] }),
    })),
  } as const;

  const options = currentConfig[historyType];

  return (
    <Autocomplete
      value={
        selectedAction
          ? options
              .map((option) => ({ ...option, label: option.label.toUpperCase() }))
              .find((option) => option.value === selectedAction)
          : null
      }
      onChange={(_e, option) => onChangeAction(option?.value as HistoryActionEnum)}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      options={options}
      renderOption={(props, option) =>
        option.value ? (
          <li {...props} key={option.value}>
            <Styled.HistoryActionOption status={option.value as HistoryActionEnum} />
          </li>
        ) : null
      }
      placeholder={formatMessage({ id: AppMessages['cards.history.filters.action'] })}
      inputVariant="outlined"
      inputSize="big"
    />
  );
};
